import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  FaReact, FaNodeJs, FaPhp, FaLaravel, FaHtml5, FaCss3Alt, 
  FaJs, FaBootstrap, FaGitAlt, FaDatabase 
} from 'react-icons/fa';
import { 
  SiFlutter, SiTailwindcss, SiMysql, SiFirebase, 
  SiCplusplus, SiJquery 
} from 'react-icons/si';

const Skills = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const skillCategories = [
    {
      title: "Frontend",
      skills: [
        { name: "HTML5", icon: <FaHtml5 />, color: "#E34F26" },
        { name: "CSS3", icon: <FaCss3Alt />, color: "#1572B6" },
        { name: "JavaScript", icon: <FaJs />, color: "#F7DF1E" },
        { name: "React.js", icon: <FaReact />, color: "#61DAFB" },
        { name: "TailwindCSS", icon: <SiTailwindcss />, color: "#06B6D4" },
        { name: "Bootstrap", icon: <FaBootstrap />, color: "#7952B3" },
      ]
    },
    {
      title: "Backend",
      skills: [
        { name: "PHP", icon: <FaPhp />, color: "#777BB4" },
        { name: "Laravel", icon: <FaLaravel />, color: "#FF2D20" },
        { name: "Node.js", icon: <FaNodeJs />, color: "#339933" },
        { name: "jQuery", icon: <SiJquery />, color: "#0769AD" },
      ]
    },
    {
      title: "Mobile Development",
      skills: [
        { name: "Flutter", icon: <SiFlutter />, color: "#02569B" },
      ]
    },
    {
      title: "Databases",
      skills: [
        { name: "MySQL", icon: <SiMysql />, color: "#4479A1" },
        { name: "Firebase", icon: <SiFirebase />, color: "#FFCA28" },
        { name: "Firestore", icon: <SiFirebase />, color: "#FFA000" },
      ]
    },
    {
      title: "Other",
      skills: [
        { name: "Git/GitHub", icon: <FaGitAlt />, color: "#F05032" },
        { name: "C++", icon: <SiCplusplus />, color: "#00599C" },
        { name: "REST API", icon: <FaDatabase />, color: "#4DB33D" },
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="skills" className="section-padding section-dark">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: -30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              Skills & Tech Stack
            </motion.h2>
            <motion.div 
              className="section-divider"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            ></motion.div>
            <motion.p 
              className="section-description"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              Technologies I work with to bring ideas to life
            </motion.p>
          </motion.div>

          <motion.div
            className="skills-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {skillCategories.map((category, catIndex) => (
              <motion.div
                key={catIndex}
                className="skill-category"
                variants={itemVariants}
              >
                <h3 className="category-title">{category.title}</h3>
                <div className="skills-list">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.div
                      key={skillIndex}
                      className="skill-badge"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 12,
                        delay: catIndex * 0.1 + skillIndex * 0.05
                      }}
                      whileHover={{ 
                        scale: 1.15, 
                        y: -8,
                        rotate: [0, -5, 5, 0],
                        transition: { 
                          type: "spring",
                          stiffness: 300,
                          damping: 10
                        }
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <motion.span 
                        className="skill-icon" 
                        style={{ color: skill.color }}
                        whileHover={{
                          rotate: 360,
                          transition: { duration: 0.6 }
                        }}
                      >
                        {skill.icon}
                      </motion.span>
                      <span className="skill-name">{skill.name}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="core-strengths"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="strengths-title">Core Strengths</h3>
            <div className="strengths-grid">
              <div className="strength-item">⚡ Build scalable Laravel & PHP systems</div>
              <div className="strength-item">⚡ Develop cross-platform mobile apps using Flutter</div>
              <div className="strength-item">⚡ Create enterprise dashboards & admin panels</div>
              <div className="strength-item">⚡ Design and integrate custom APIs</div>
              <div className="strength-item">⚡ Develop SaaS platforms and event-based systems</div>
              <div className="strength-item">⚡ UI/UX consistency and performance optimization</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
